import { BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import { calculateEdgePath } from 'components/knowledgeGraph/utils/edgeUtils';
import { useMemo } from 'react';
import { getEdgeStyles } from './styles';

/**
 * Calculate the appropriate angle for the edge label
 */
const getAngleDeg = (sourceX, sourceY, targetX, targetY) => {
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;

  // If the line angle is "pointing" backwards (beyond ±90), flip by 180:
  if (angleDeg > 90) {
    angleDeg -= 180;
  } else if (angleDeg < -90) {
    angleDeg += 180;
  }

  return angleDeg;
};

/**
 * Custom edge component that creates a floating edge between nodes
 */
export const FloatingEdge = ({ id, source, target, markerEnd, style, label, data }) => {
  const { isSimplifiedView } = data || {};
  const { classes, cx } = getEdgeStyles();

  // If we don't have the necessary data, render a basic edge
  if (!data || !data.edgeParams) {
    return (
      <BaseEdge
        id={id}
        path="M 0 0 L 0 0" // Empty path as fallback
        style={{ ...style, opacity: 0 }} // Make it invisible
      />
    );
  }

  // Get source and target handles from edge params
  const { sourceX, sourceY, targetX, targetY } = data.edgeParams;

  const { edgePath, labelX, labelY, angleDeg } = useMemo(() => {
    // Generate the appropriate path based on whether curves are enabled
    // Use our custom edge path calculation
    const [edgePath, labelX, labelY] = calculateEdgePath(data.edgeParams, data.curve);

    // Compute angle for label rotation
    const angleDeg = getAngleDeg(sourceX, sourceY, targetX, targetY);

    return { edgePath, labelX, labelY, angleDeg };
  }, [data.edgeParams, data.curve, sourceX, sourceY, targetX, targetY]);

  return (
    <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} label={null} data-source={source} data-target={target}>
      <EdgeLabelRenderer>
        {label && (
          <div
            data-id={id}
            className={cx(classes.edgeLabel, isSimplifiedView && classes.simplifiedLabel)}
            style={{
              left: labelX,
              top: labelY,
              transform: `translate(-50%, -50%) rotate(${angleDeg}deg)`
            }}
          >
            {label}
          </div>
        )}
      </EdgeLabelRenderer>
    </BaseEdge>
  );
};

// Default edge is just our floating edge
export const DefaultEdge = props => <FloatingEdge {...props} />;

export const edgeTypes = {
  floating: FloatingEdge,
  default: DefaultEdge
};
